import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  Radio,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import DynamicTable from "components/UI/dynamicTable/DynamicTable";
import HorizontalLine from "components/UI/horizontalLine/HorizontalLine";
import InputField from "components/UI/inputField/InputField";
import { NoDataSurveyCompleted } from "components/widgets/phase-3/esgMaturity/shareSurvey/NoDataSurveyCompleted";
import logo from "images/phase-3/stirrup_logo/stirrup-logo.svg";
import surveyOpenClosedImage from "images/phase-3/materialitySurvey/Survey_open_closed.png";
import surveySubmittedImage from "images/phase-3/materialitySurvey/survey_submited.png";
import moment from "moment";
import { useState } from "react";
import { useParams } from "react-router-dom";
import apiBolerplate from "utils/_helpers/apiBolerplate";
import NoSurveyDataComponent from "components/widgets/phase-3/esgMaturity/shareSurvey/NoSurveyDataComponent";

function MaterialityQuestion({ hasEditable = false }) {
  const [formData, setFormData] = useState({
    financial_ratings: [],
    impact_ratings: [],
  });
  const [justSubmitted, setJustSubmitted] = useState(false);
  const { category, id: materiality_assessment_id } = useParams();

  const { data: surveyData } = useQuery({
    queryKey: ["materiality-survey", materiality_assessment_id, category],
    queryFn: async () => {
      const res = await apiBolerplate({
        url: "materiality_assessment_new/survey/get_survey",
        bodyData: { materiality_assessment_id, category },
        hideMsg: true,
      });
      return res?.status ? res.data : null;
    },
    enabled: !!materiality_assessment_id,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const handleChange = ({ target: { name, value } }) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTableChange = (materialTopicId, type, answer) => {
    setFormData((prev) => {
      const ratingKey =
        type === "financial" ? "financial_ratings" : "impact_ratings";
      const existingRatings = prev[ratingKey] || [];

      const filteredRatings = existingRatings.filter(
        (r) => r.material_topic_id !== materialTopicId
      );

      const updatedRatings = [
        ...filteredRatings,
        { material_topic_id: materialTopicId, answer },
      ];

      return {
        ...prev,
        [ratingKey]: updatedRatings,
      };
    });
  };

  const topics = surveyData?.material_topics || [];
  const stakeholderGroups = surveyData?.stakeholder_groups || [];
  const typeOfMateriality = surveyData?.type_of_materiality;

  const ratingOptions = [
    "Not Important at all",
    "Slightly Important",
    "Moderately Important",
    "Very Important",
    "Extremely Important",
  ];

  const impactRatingOptions = [
    "No Impact",
    "Low Impact",
    "Moderate Impact",
    "High Impact",
    "Very High Impact",
  ];

  const fixedQuestions = [
    {
      question:
        "Please select the stakeholder group that best represents your relationship with the organisation ?",
      option: stakeholderGroups.map((group, index) => ({
        value: index + 1,
        option: group,
      })),
      name: "stakeholder_group",
    },
    {
      question:
        "How long have you been working with your current organization in your present role (as an employee, supplier, investor, regulator, etc.)?",
      option: [
        { value: 1, option: "Less than 1 year" },
        { value: 2, option: "Between 1 to 3 years" },
        { value: 3, option: "More than 3 years" },
      ],
      name: "tenure",
    },
    {
      question:
        "As per your understanding, how important are the below mentioned factors to the company's performance?",
      table: true,
      type: "financial",
      descriptionKey: "financial_materiality_description",
    },
    ...(typeOfMateriality?.toLowerCase() === "double materiality"
      ? [
          {
            question:
              "How much do you think that the company's operation affects the following: ",
            table: true,
            type: "impact",
            descriptionKey: "impact_materiality_description",
          },
        ]
      : []),
    {
      question:
        "What are the other topics, over and above the topics mentioned, should the organisation focus on?",
      text: true,
      name: "other_topics_comment",
    },
  ];

  const allFinancialAnswered = topics.every((topic) =>
    formData.financial_ratings.some(
      (r) => r.material_topic_id === topic.material_topic_id
    )
  );

  const allImpactAnswered = fixedQuestions.some((q) => q.type === "impact")
    ? topics.every((topic) =>
        formData.impact_ratings.some(
          (r) => r.material_topic_id === topic.material_topic_id
        )
      )
    : true;

  const isDisabled = !(
    formData?.stakeholder_group &&
    formData?.tenure &&
    allFinancialAnswered &&
    allImpactAnswered
  );

  const handleSubmit = async () => {
    await apiBolerplate({
      url: "materiality_assessment_new/survey/submit_survey",
      bodyData: {
        materiality_assessment_id,
        category,
        stakeholder_group: formData.stakeholder_group,
        tenure: formData.tenure,
        financial_ratings: formData.financial_ratings,
        impact_ratings: formData.impact_ratings,
        other_topics_comment: formData.other_topics_comment || "",
      },
      messageType: "success",
      customMessage: "Survey submitted successfully",
      callback: () => {
        setFormData({ financial_ratings: [], impact_ratings: [] });
        setJustSubmitted(true);
      },
    });
  };
  const isExpired = moment().isAfter(
    moment(surveyData?.end_date, "DD-MM-YYYY"),
    "day"
  );

  const isNotStarted = moment().isBefore(
    moment(surveyData?.start_date, "DD-MM-YYYY"),
    "day"
  );

  if (!surveyData) {
    return null;
  }

  // Format dates dynamically
  const formatSurveyDate = (dateString) => {
    if (!dateString) return "";
    return moment(dateString, "DD-MM-YYYY").format("MMMM D, YYYY");
  };

  // Check if user has just submitted the survey
  if (justSubmitted) {
    return (
      <NoSurveyDataComponent
        variant="submitted"
        image={surveySubmittedImage}
        // dateText={`Submitted on ${formatSurveyDate(moment().format("DD-MM-YYYY"))}`}
      />
    );
  }

  if (isNotStarted) {
    return (
      <NoSurveyDataComponent
        variant="not_available"
        image={surveyOpenClosedImage}
        dateText={`${formatSurveyDate(surveyData?.start_date)}`}
      />
    );
  }

  if (isExpired) {
    return (
      <NoSurveyDataComponent
        variant="ended"
        image={surveyOpenClosedImage}
        dateText={`${formatSurveyDate(surveyData?.end_date)}`}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          background: "#fff",
          p: 2,
          width: "90%",
          margin: "auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <img
            src={logo}
            style={{
              width: "30%",
              height: "25%",
              objectFit: "contain",
            }}
          />
          <Typography
            variant="h2"
            fontWeight={600}
            sx={{ lineHeight: 1.4 }}
            marginTop={0.5}
          >
            {surveyData.assessment_name}
          </Typography>
        </Box>
        <Typography
          variant="h2"
          fontWeight={600}
          sx={{ lineHeight: 1.4 }}
          marginTop={0.5}
        >
          End Date: {surveyData.end_date}
        </Typography>
      </Box>
      <HorizontalLine />

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: 2,
          width: "90%",
          margin: "0 auto",
          pointerEvents: !hasEditable && "none",
          paddingTop: 0,
        }}
      >
        <Paper elevation={3} sx={{ padding: 2 }}>
          {fixedQuestions.map((data, index) => (
            <Box key={index} mb={3}>
              <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                <Box
                  sx={{
                    border: "1px solid #E9E9E9",
                    borderRadius: "8px",
                    padding: "16px",
                    width: "50px",
                  }}
                >
                  Q{index + 1}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    border: "1px solid #E9E9E9",
                    borderRadius: "8px",
                    padding: "16px",
                    width: "100%",
                  }}
                >
                  {data?.question}
                  {index !== fixedQuestions.length - 1 && (
                    <span
                      style={{
                        color: "red",
                        marginLeft: "4px",
                        fontSize: "1.1rem",
                      }}
                    >
                      *
                    </span>
                  )}
                </Typography>
              </Box>

              {data?.option && (
                <Box sx={{ padding: "16px 10px" }}>
                  <Box display="flex" flexWrap="wrap" gap={3}>
                    {data.option.map((opt) => (
                      <FormControlLabel
                        key={opt.value}
                        value={opt.value}
                        control={
                          <Radio
                            checked={
                              formData[data.name] === (opt.option || opt.value)
                            }
                            onChange={() =>
                              handleChange({
                                target: {
                                  name: data.name,
                                  value: opt.option || opt.value,
                                },
                              })
                            }
                          />
                        }
                        label={opt.option || opt.value}
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {data?.table && (
                <DynamicTable
                  headings={[
                    "Topic",
                    "Description",
                    ...(data.type === "financial"
                      ? ratingOptions
                      : impactRatingOptions),
                  ]}
                  stickyHeader
                >
                  {topics?.map((topic) => {
                    const ratingKey =
                      data.type === "financial"
                        ? "financial_ratings"
                        : "impact_ratings";
                    const currentRating = formData[ratingKey]?.find(
                      (r) => r.material_topic_id === topic.material_topic_id
                    );

                    return (
                      <TableRow key={topic.material_topic_id + data.type}>
                        <TableCell sx={{ width: "20%" }}>
                          {topic.name}
                        </TableCell>
                        <TableCell sx={{ width: "35%" }}>
                          {topic[data.descriptionKey]}
                        </TableCell>
                        {(data.type === "financial"
                          ? ratingOptions
                          : impactRatingOptions
                        ).map((option, idx) => (
                          <TableCell
                            key={`${data.type}_${idx}`}
                            sx={{ width: "9%", textAlign: "center" }}
                          >
                            <Radio
                              checked={currentRating?.answer === option}
                              onChange={() =>
                                handleTableChange(
                                  topic.material_topic_id,
                                  data.type,
                                  option
                                )
                              }
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </DynamicTable>
              )}

              {data?.text && (
                <InputField
                  heading={false}
                  label="Enter Your Suggestion"
                  rows={3}
                  name={data?.name}
                  value={formData[data?.name] || ""}
                  handleChange={handleChange}
                />
              )}
            </Box>
          ))}
        </Paper>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          position: "sticky",
          bottom: 0,
          right: 0,
          background: "#fff",
          p: 2,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isDisabled}
          sx={{ height: "40px", textTransform: "none", marginRight: "8rem" }}
        >
          Submit Survey
        </Button>
      </Box>
    </Box>
  );
}

export default MaterialityQuestion;
